import { setTurnstileToken as storeTurnstileToken } from '@/lib/turnstile-token-manager';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Clock, Loader2, Shield, Users } from 'lucide-react';

import { Turnstile } from 'react-turnstile';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TemplateConfig } from './DynamicFormContext';

interface WelcomeScreenProps {
  config: TemplateConfig | null;
  turnstileStatus: string;
  turnstileToken: string | null;
  tosAccepted: boolean;
  tosTransitioning: boolean;
  setTurnstileStatus: (status: 'success' | 'error' | 'expired' | 'required') => void;
  setTurnstileToken: (token: string | null) => void;
  setTosTransitioning: (transitioning: boolean) => void;
  setTosAccepted: (accepted: boolean) => void;
  setShowWelcome: (show: boolean) => void;
}

const WelcomeScreen = ({
  config,
  turnstileStatus,
  setTurnstileStatus,
  setTurnstileToken,
  tosAccepted,
  tosTransitioning,
  setTosTransitioning,
  turnstileToken,
  setTosAccepted,
  setShowWelcome,
}: WelcomeScreenProps) => {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-dvh overflow-y-auto bg-[hsl(var(--bg))] px-3 sm:px-4 py-4 sm:py-6 md:py-8 lg:py-6 flex items-center justify-center">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,hsla(var(--brand-primary)/0.18),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom,hsla(var(--brand-primary)/0.1),transparent_65%)]" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative mx-auto w-full max-w-4xl rounded-2xl md:rounded-3xl border border-brand-border bg-background p-4 sm:p-6 md:p-8 lg:p-6 xl:p-10 text-center shadow-xl backdrop-blur-sm"
      >
        <div className="mx-auto mb-3 sm:mb-4 lg:mb-3 xl:mb-6 flex h-12 sm:h-14 lg:h-12 xl:h-16 w-auto items-center justify-center">
          <img
            src="/Selise Legal Templates.svg"
            alt="SELISE Legal Templates"
            width={424}
            height={241}
            className="h-10 sm:h-12 lg:h-10 xl:h-14 w-auto"
          />
        </div>
        <h1 className="mb-2 sm:mb-3 lg:mb-2 xl:mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-2xl xl:text-3xl font-semibold text-[hsl(var(--fg))] font-heading">
          {config?.title}
        </h1>
        <p className="mb-4 sm:mb-6 lg:mb-4 xl:mb-8 text-sm sm:text-base md:text-lg lg:text-base xl:text-lg leading-relaxed text-brand-muted line-clamp-2 ">
          {config?.description}
          <br />
          <span className="text-brand-muted">
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_SUBTITLE2')}
          </span>
        </p>

        {/* Trust Stats */}
        <div className="mb-4 sm:mb-5 lg:mb-3 xl:mb-6 grid grid-cols-3 gap-2 sm:gap-4 rounded-xl sm:rounded-2xl border border-brand-border bg-brand-surface p-3 sm:p-4 lg:p-3 xl:p-5">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 sm:gap-2 mb-0.5 sm:mb-1">
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-primary" />
              <span className="text-base sm:text-xl lg:text-lg xl:text-2xl font-bold text-[hsl(var(--fg))]">
                10K+
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-brand-muted">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_DOCUMENTSGENERATED')}
            </p>
          </div>
          <div className="text-center border-x border-brand-border">
            <div className="flex items-center justify-center gap-1 sm:gap-2 mb-0.5 sm:mb-1">
              <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-primary" />
              <span className="text-base sm:text-xl lg:text-lg xl:text-2xl font-bold text-[hsl(var(--fg))]">
                98%
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-brand-muted">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_SATISFACTIONRATE')}
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 sm:gap-2 mb-0.5 sm:mb-1">
              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-primary" />
              <span className="text-base sm:text-xl lg:text-lg xl:text-2xl font-bold text-[hsl(var(--fg))]">
                &lt;5m
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-brand-muted">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_AVERAGETIME')}
            </p>
          </div>
        </div>

        {/* Before you continue - Combined ToS and Verification */}
        <div className="mb-3 sm:mb-4 lg:mb-3 xl:mb-5 rounded-lg border border-brand-border bg-card p-3 sm:p-4 lg:p-3 xl:p-4">
          <div className="mb-2 sm:mb-3 lg:mb-2 xl:mb-4 text-center">
            <h3 className="text-xs sm:text-sm font-medium text-[hsl(var(--fg))] font-heading">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_BEFOREYOUCONTINUE')}
            </h3>
          </div>

          {/* Human Verification */}
          {import.meta.env.VITE_TURNSTILE_SITE_KEY ? (
            <div className="flex flex-col items-center gap-3">
              <Turnstile
                sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                retry="auto"
                refreshExpired="auto"
                // sandbox={false}
                theme="light"
                onError={() => {
                  setTurnstileStatus('error');
                  setTurnstileToken(null);
                }}
                onExpire={() => {
                  setTurnstileStatus('expired');
                  setTurnstileToken(null);
                }}
                onLoad={() => {
                  setTurnstileStatus('required');
                }}
                onVerify={(token) => {
                  setTurnstileStatus('success');
                  setTurnstileToken(token);
                  storeTurnstileToken(token);
                }}
              />
              {turnstileStatus === 'error' && (
                <div className="flex items-center justify-center gap-2 text-sm text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_VERIFICATIONFAILED')}</span>
                </div>
              )}
              {turnstileStatus === 'expired' && (
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_VERIFICATIONEXPIRED')}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
              <AlertTriangle className="mx-auto h-5 w-5 text-amber-600 mb-2" />
              <p className="text-sm text-amber-800 font-medium mb-1">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_TURNSTILECONFIGMISSING')}
              </p>
              <p className="text-xs text-amber-700">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_TURNSTILECONFIGDESCRIPTION')}
              </p>
            </div>
          )}
        </div>

        {/* Terms of Service Consent Card */}
        <motion.button
          type="button"
          onClick={() => {
            if (
              turnstileStatus === 'success' &&
              turnstileToken &&
              !tosAccepted &&
              !tosTransitioning
            ) {
              setTosAccepted(true);
              setTosTransitioning(true);
              // Show loading animation for 1.8 seconds before proceeding
              setTimeout(() => {
                setShowWelcome(false);
              }, 1800);
            }
          }}
          disabled={turnstileStatus !== 'success' || !turnstileToken || tosAccepted}
          className={`
              group relative w-full max-w-2xl mx-auto rounded-2xl md:rounded-3xl border-2 px-4 py-3 sm:px-6 sm:py-4 lg:px-5 lg:py-3 xl:px-8 xl:py-5 text-left transition-all duration-300 select-none
              active:scale-[0.98] touch-manipulation
              ${
                tosAccepted
                  ? 'border-brand-primary bg-linear-to-br from-[hsl(var(--brand-primary)/_0.08)] to-[hsl(var(--brand-primary)/_0.02)] shadow-xl shadow-[hsl(var(--brand-primary)/_0.15)]'
                  : 'border-brand-border bg-card hover:border-brand-primary hover:bg-[hsl(var(--brand-primary)/_0.02)] hover:shadow-lg'
              }
              ${turnstileStatus !== 'success' || !turnstileToken ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
            `}
          whileHover={{
            scale: turnstileStatus === 'success' && turnstileToken ? 1.015 : 1,
          }}
          whileTap={{
            scale: turnstileStatus === 'success' && turnstileToken ? 0.985 : 1,
          }}
        >
          <div className="flex items-center gap-3 sm:gap-4 md:gap-5">
            {/* Custom Checkbox - Larger for touch with smooth animation */}
            <motion.div
              className={`
                  shrink-0 h-7 w-7 sm:h-8 sm:w-8 md:h-7 md:w-7 rounded-lg border-2 flex items-center justify-center
                  ${
                    tosAccepted
                      ? 'border-brand-primary bg-brand-primary shadow-md shadow-[hsl(var(--brand-primary)/_0.3)]'
                      : 'border-[hsl(var(--brand-muted)/_0.3)] bg-background group-hover:border-brand-primary group-hover:bg-[hsl(var(--brand-primary)/_0.05)]'
                  }
                `}
              initial={false}
              animate={{
                scale: tosAccepted ? [1, 1.15, 1] : 1,
                backgroundColor: tosAccepted ? 'hsl(var(--brand-primary))' : 'hsl(var(--bg))',
              }}
              transition={{
                scale: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
                backgroundColor: { duration: 0.25, ease: 'easeOut' },
              }}
            >
              <svg
                className="h-4 w-4 sm:h-5 sm:w-5 md:h-4 md:w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <motion.path
                  d="M5 12l5 5L19 7"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{
                    pathLength: tosAccepted ? 1 : 0,
                    opacity: tosAccepted ? 1 : 0,
                  }}
                  transition={{
                    pathLength: {
                      duration: 0.4,
                      delay: tosAccepted ? 0.1 : 0,
                      ease: [0.65, 0, 0.35, 1],
                    },
                    opacity: {
                      duration: 0.15,
                      delay: tosAccepted ? 0.05 : 0,
                    },
                  }}
                />
              </svg>
            </motion.div>

            {/* Text Content */}
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm sm:text-base md:text-base lg:text-sm xl:text-base leading-relaxed transition-colors duration-300 ${tosAccepted ? 'text-[hsl(var(--fg))]' : 'text-[hsl(var(--fg)/_0.7)]'}`}
              >
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_TOSCHECKBOXLABEL')}{' '}
                <Link
                  to="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline font-semibold underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_TERMSOFSERVICE')}
                </Link>{' '}
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_AND')}{' '}
                <Link
                  to="https://selisegroup.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline font-semibold underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_PRIVACYPOLICY')}
                </Link>
              </p>
            </div>

            {/* Arrow indicator */}
            <motion.div
              className={`shrink-0 transition-colors duration-300 ${tosAccepted ? 'text-brand-primary' : 'text-[hsl(var(--brand-muted)/_0.4)] group-hover:text-brand-primary'}`}
              animate={{ x: tosAccepted ? 0 : [0, 4, 0] }}
              transition={{
                duration: 1.5,
                repeat: tosAccepted ? 0 : Infinity,
                ease: 'easeInOut',
              }}
            >
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
            </motion.div>
          </div>

          {/* Success feedback with loading animation */}
          <AnimatePresence>
            {tosAccepted && tosTransitioning && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <div className="mt-5 pt-5 border-t border-[hsl(var(--brand-primary)/_0.2)]">
                  {/* Loading progress bar */}
                  <div className="relative w-full h-1.5 bg-[hsl(var(--brand-primary)/_0.15)] rounded-full overflow-hidden mb-4">
                    <motion.div
                      className="absolute inset-y-0 left-0 bg-linear-to-r from-brand-primary to-[hsl(var(--brand-primary)/_0.7)] rounded-full"
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 1.6, ease: [0.4, 0, 0.2, 1] }}
                    />
                    {/* Shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-linear-to-r from-transparent via-white/30 to-transparent"
                      initial={{ x: '-100%' }}
                      animate={{ x: '200%' }}
                      transition={{ duration: 1, repeat: 2, ease: 'linear' }}
                    />
                  </div>

                  {/* Loading text with animated dots */}
                  <div className="flex items-center justify-center gap-3 text-brand-primary">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                    >
                      <Loader2 className="h-5 w-5" />
                    </motion.div>
                    <span className="text-sm font-medium">
                      {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_PREPARINGAGREEMENT')}
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0] }}
                        transition={{
                          duration: 1.2,
                          repeat: Infinity,
                          times: [0, 0.5, 1],
                        }}
                      >
                        ...
                      </motion.span>
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        {turnstileStatus !== 'success' || !turnstileToken ? (
          <p className="mt-2 sm:mt-3 lg:mt-2 xl:mt-4 text-xs sm:text-sm text-brand-muted">
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_COMPLETEVERIFICATION')}
          </p>
        ) : !tosAccepted ? (
          <p className="mt-2 sm:mt-3 lg:mt-2 xl:mt-4 text-xs sm:text-sm text-brand-muted flex items-center justify-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-brand-primary animate-pulse" />
            {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_TAPTOAGREE')}
          </p>
        ) : null}
      </motion.div>
    </div>
  );
};

export default WelcomeScreen;
